import React, { useState } from 'react';
import { locationApi } from '../api/axios';
import { Message } from './charte/Elements';
import { DocumentClient, formatTotal, messageErreurPdf, telechargerPdf } from '../donnees/documents';
import type { Mission, SignatureDevisServeur } from '../donnees/missions';
import { jourCourt } from '../donnees/situation';
import { messageSignatureDevis } from '../utils/messagesErreur';

/* Devis à signer, d'après la maquette Documents : bloc indigo avec le
   détail du devis (lignes, TVA, total TTC) et, à droite, la signature.

   Côté serveur (location-service) : PATCH /quest/{questId}/paiement
   { statutPaiement: "devis_signe", devisNumero, conditionsAcceptees: true }.
   La signature est acceptée dès qu'admin-service confirme un devis de
   modèle 2 pour la mission. Le serveur renvoie la mission avec sa trace
   « signatureDevis » : numéro et montant TTC du devis signé, acceptation des
   conditions, horodatage serveur, adresse IP et compte signataire. C'est
   cette trace qui s'affiche, jamais l'heure du navigateur. */

export interface ResultatSignature { devis: DocumentClient; mission: Mission; trace: SignatureDevisServeur | null }

const nombre = (n?: number | null) => n == null ? '—' : Math.round(n).toLocaleString('fr-FR').replace(/ /g, ' ');
const pourcent = (taux: number) => `${String(Math.round(taux * 10000) / 100).replace('.', ',')} %`;
const PALE = 'rgba(242,232,212,0.6)';

/** Durée prévue de la collecte, en semaines ou en jours. */
function duree(debut?: string, fin?: string): string | null {
  if (!debut || !fin) return null;
  const jours = Math.round((new Date(fin).getTime() - new Date(debut).getTime()) / 86400000);
  if (!Number.isFinite(jours) || jours <= 0) return null;
  if (jours >= 14 && jours % 7 === 0) return `${jours / 7} semaines`;
  return `${jours} jour${jours > 1 ? 's' : ''}`;
}

const Fait: React.FC<{ libelle: string; valeur: string }> = ({ libelle, valeur }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <span style={{ fontSize: '12px', color: PALE }}>{libelle}</span>
    <span style={{ fontSize: '16px', fontWeight: 600 }}>{valeur}</span>
  </div>
);

const Ligne: React.FC<{ libelle: string; montant: string }> = ({ libelle, montant }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '14px', color: 'rgba(242,232,212,0.8)' }}>
    <span>{libelle}</span><span className="mono">{montant}</span>
  </div>
);

const SignatureDevis: React.FC<{
  devis: DocumentClient;
  mission?: Mission;
  titreMission: string;
  enVue?: boolean;
  onSigne: (r: ResultatSignature) => void;
}> = ({ devis, mission, titreMission, enVue, onSigne }) => {
  const [accepte, setAccepte] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [pdfEnCours, setPdfEnCours] = useState(false);

  const signer = async () => {
    if (!accepte || !devis.questId) return;
    setEnvoi(true);
    setErreur('');
    try {
      const res = await locationApi.patch(`/quest/${devis.questId}/paiement`, {
        statutPaiement: 'devis_signe',
        devisNumero: devis.numero,
        conditionsAcceptees: true,
      });
      const m: Mission = res.data;
      onSigne({ devis, mission: m, trace: m?.signatureDevis ?? null });
    } catch (err) {
      setErreur(messageSignatureDevis(err));
    } finally {
      setEnvoi(false);
    }
  };

  const lirePdf = async () => {
    setPdfEnCours(true);
    setErreur('');
    try { await telechargerPdf(devis); } catch (err) { setErreur(messageErreurPdf(devis, err)); } finally { setPdfEnCours(false); }
  };

  // TVA regroupée par taux, comme sur le PDF ; un seul montant si les lignes ne donnent pas le taux.
  const lignes = devis.lignes ?? [];
  const taux = new Map<number, number>();
  for (const l of lignes) if (l.tauxTva != null && l.montantTva) taux.set(l.tauxTva, (taux.get(l.tauxTva) ?? 0) + l.montantTva);
  const titreId = `titre-devis-${devis.id}`;
  const d = duree(mission?.dateDebut, mission?.dateFin);

  return (
    <section id={`devis-${devis.id}`} aria-labelledby={titreId} data-testid={`a-signer-${devis.id}`} className="bloc-signature"
      style={{ outline: enVue ? '3px solid var(--orange-clair)' : undefined, outlineOffset: '3px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div className="etiquette-mono" style={{ color: 'var(--orange-clair)' }}>
          Devis à signer · {devis.numero} · émis le {jourCourt(devis.emisLe)}
        </div>
        <h2 id={titreId} style={{ margin: 0, fontFamily: 'var(--police-titre)', fontWeight: 500, fontSize: '26px', lineHeight: 1.15 }}>{titreMission}</h2>
        <div className="grille-faits">
          {mission?.collectesAttendues ? <Fait libelle="Lieux ciblés" valeur={nombre(mission.collectesAttendues)} /> : null}
          {d && <Fait libelle="Durée" valeur={d} />}
          <Fait libelle="Livrables" valeur="Portail · Excel" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid rgba(242,232,212,0.18)', paddingTop: '14px' }}>
          {lignes.length > 0
            ? lignes.map((l, i) => <Ligne key={i} libelle={l.libelle} montant={nombre(l.montantHt)} />)
            : <Ligne libelle="Montant HT" montant={nombre(devis.montantHt)} />}
          {taux.size > 0
            ? Array.from(taux.entries()).map(([t, m]) => <Ligne key={t} libelle={`TVA ${pourcent(t)}`} montant={nombre(m)} />)
            : <Ligne libelle="TVA" montant={nombre(devis.montantTva)} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginTop: '4px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '15px' }}>Total TTC</span>
            <span style={{ fontFamily: 'var(--police-titre)', fontSize: '30px' }}>
              {nombre(devis.montantTtc)}{' '}
              <span style={{ fontSize: '14px', fontFamily: 'var(--police-texte)', color: 'rgba(242,232,212,0.7)' }}>F CFA (XOF)</span>
            </span>
          </div>
        </div>
      </div>

      <div className="carte-signature">
        <div style={{ fontWeight: 600, fontSize: '15px' }}>Signer ce devis</div>
        <div style={{ fontSize: '13px', color: 'rgba(242,232,212,0.75)', lineHeight: 1.5 }}>
          La signature vaut acceptation du devis et des conditions générales de Tribe Sénégal SAS. Elle est horodatée et vous
          recevez une confirmation par courriel.
        </div>
        <label className="case" style={{ fontSize: '13px' }}>
          <input type="checkbox" checked={accepte} onChange={e => setAccepte(e.target.checked)} disabled={envoi} />
          <span>
            J'ai lu le devis {devis.numero} et j'accepte les conditions générales, pour un montant engagé de{' '}
            {formatTotal(devis.montantTtc)} TTC. Je suis habilité à engager mon organisation.
          </span>
        </label>
        {erreur && <Message genre="erreur">{erreur}</Message>}
        <button type="button" className="bouton bouton--grand" onClick={signer} disabled={!accepte || envoi}
          aria-label={`Signer le devis ${devis.numero}`}>
          {envoi ? 'Signature…' : 'Signer le devis'}
        </button>
        <button type="button" className="lien-ocre" onClick={lirePdf} disabled={pdfEnCours}>
          {pdfEnCours ? 'Téléchargement…' : 'Télécharger le PDF avant de signer'}
        </button>
      </div>
    </section>
  );
};

export default SignatureDevis;
